import type { AxiosResponse } from "axios";
import { isAxiosError, isCancel } from "axios";

/**
 * Why a request failed transiently, chosen so a screen can say something
 * truthful about what went wrong without knowing any HTTP.
 */
export type TransientFailureReason = "connectivity" | "server" | "session";

/**
 * How a failed API call deserves to be surfaced.
 *
 * `aborted` is not a failure: the request was torn down because the caller
 * superseded or unmounted it. There is nobody left to tell and nothing to
 * report.
 *
 * `transient` is a failure the identical request could survive on a later
 * attempt — the connection, the server, or the session. The caller offers a
 * retry rather than destroying what the user was working on.
 *
 * `fatal` is the request itself being unacceptable. Repeating it verbatim
 * yields the same answer, so offering a retry would be a lie.
 */
export type ApiFailure =
  | { kind: "aborted" }
  | { kind: "transient"; reason: TransientFailureReason }
  | { kind: "fatal"; message: string | undefined };

/**
 * Classify the outcome of an `axios-hooks` request, or `null` when nothing has
 * failed — including when no response has arrived yet.
 *
 * Both halves of the state are needed because a failure arrives on one of two
 * channels: `axios-hooks` keeps a rejection and a response in separate fields,
 * and `apiAxios` is configured to resolve some error statuses rather than
 * reject them.
 */
export function classifyApiFailure(
  error: unknown,
  response: AxiosResponse | undefined,
): ApiFailure | null {
  if (error === null || error === undefined) {
    return response === undefined ? null : classifyStatus(response);
  }

  // A cancellation is an `AxiosError` too, so it has to be recognised first.
  if (isCancel(error)) {
    return { kind: "aborted" };
  }

  if (isAxiosError(error)) {
    // Without a response the exchange never completed: either the request did
    // not reach the server or its reply did not get back. The request itself
    // was never judged, so it is worth making again.
    return error.response === undefined
      ? { kind: "transient", reason: "connectivity" }
      : classifyStatus(error.response);
  }

  return {
    kind: "fatal",
    message: error instanceof Error ? error.message : undefined,
  };
}

function classifyStatus(response: AxiosResponse): ApiFailure | null {
  const { status } = response;

  if (status >= 200 && status < 300) {
    return null;
  }

  // A lapsed or not-yet-renewed session says nothing about the request; signing
  // in again is what fixes it.
  if (status === 401) {
    return { kind: "transient", reason: "session" };
  }

  // The server declining to answer now is not the same as refusing the request.
  if (status === 408 || status === 429 || status >= 500) {
    return { kind: "transient", reason: "server" };
  }

  return { kind: "fatal", message: refusalReason(response) };
}

/**
 * The server's own account of why it refused, which our controllers return as
 * a `reason` field.
 */
export function refusalReason(response: AxiosResponse): string | undefined {
  const body: unknown = response.data;

  if (typeof body !== "object" || body === null || !("reason" in body)) {
    return undefined;
  }

  const { reason } = body as { reason: unknown };
  return typeof reason === "string" ? reason : undefined;
}
